import {
	ColumnsProps,
	ScrollType,
	buildColumn,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	handleResponse,
	invertSelectedRowKeys,
	useHandleOnChangeTableLogic,
} from '@/Helper';
import { deviceAdminApi, graphicApi } from '@/api';
import { EditableCell, ModalConfirmation } from '@/components/common';
import { SubPermissions, User, getPermissionErrorMessage } from '@/model/AccountModel';
import { BadgeListInfo, BadgeListInfoTable } from '@/model/BadgeModel';
import { PaginationSetting, ResponseObject, ResponseStatusCode } from '@/model/CommonModel';
import { DeviceObjectType } from '@/model/DeviceAdminModel';
import { GraphicDirectoryInfo } from '@/model/GraphicModel';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Form, Table, notification } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import { TableRowSelection } from 'antd/lib/table/interface';
import React, { CSSProperties, Key, useEffect, useRef, useState } from 'react';
import styles from './listView.module.scss';

const scroll: ScrollType = { y: '50vh', x: 'max-content' };
const inputStyles: CSSProperties = { minWidth: '20em' };
const maxLength: number = 50;
const user: User = getUser();
const canEditBadge: boolean = User.getSubComponentPermission(user, SubPermissions.Badges_Badges_Edit).allowed;

type Props = {
	badgeListInfo: BadgeListInfo[];
	pagination: PaginationSetting;
	setPagination: (pagination: PaginationSetting) => void;
	loadData: (pagination?: PaginationSetting) => void;
	changeActionButtonState?: (state: boolean) => void;
	handleOnOpenBadge?: (badgeInfo: Partial<GraphicDirectoryInfo>) => void;
	selectedKeys?: React.Key[];
	setSelectedKeys?: React.Dispatch<React.SetStateAction<React.Key[]>>;
	showActions?: boolean;
};

const ListView: React.FC<Props> = ({
	badgeListInfo,
	pagination,
	setPagination,
	loadData,
	changeActionButtonState,
	handleOnOpenBadge,
	selectedKeys,
	setSelectedKeys,
	showActions = true,
}) => {
	//#region State Management Variables
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [editingKey, setEditingKey] = useState<Key>(undefined);
	const [tableData, setTableData] = useState<BadgeListInfoTable[]>([]);
	const [tableRefresh, setTableRefresh] = useState<boolean>(false);

	const isSearchPerformed = useRef<boolean>(false);
	const editingKeyReference = React.useRef<Key>(editingKey);
	const setEditKeyReference = (editingKey: Key): void => {
		editingKeyReference.current = editingKey;
		setEditingKey(editingKey);
		changeActionButtonState(editingKey !== undefined);
	};

	const [form] = Form.useForm();
	//#endregion

	useEffect(() => {
		setTableData(createTableData(badgeListInfo as BadgeListInfoTable[]));
	}, [JSON.stringify(badgeListInfo)]);

	const isEditing = (record: BadgeListInfoTable) => record.key === editingKeyReference.current;
	const isDisabled = () => editingKeyReference.current !== undefined || !canEditBadge;

	const columns: ColumnsProps<BadgeListInfoTable>[] = [
		{
			...buildColumn(' ', 'PreviewImage', '100px', 'start'),
			sorter: false,
			render: (_, row) => (
				<div className={styles.previewImage}>
					{row?.PreviewImage ? (
						<img width={50} src={`data:image/bmp;base64,${row?.PreviewImage}`} alt={'Preview'} />
					) : (
						<img width={50} src={`data:image/bmp;base64,`} alt={'NoPreview'} />
					)}
				</div>
			),
		},
		{
			...buildColumn(_('Name'), 'Name', 'auto', 'start'),
			editable: true,
			sorter: !isEditMode,
		},
		{
			...buildColumn(_('LastUpdated'), 'LastUpdated', '11em', 'start'),
			sorter: !isEditMode,
		},
		{
			...buildColumn(_('CreatedBy'), 'CreatedBy', '19em', 'start'),
			sorter: !isEditMode,
		},
	];

	if (showActions === true) {
		columns.push({
			...buildColumn(' ', 'Action', isDisabled() ? '160px' : '100px', 'start'),
			sorter: false,
		});
	}

	const mergedColumns = columns.map(col =>
		!col.editable
			? col
			: {
					...col,
					onCell: (record: BadgeListInfoTable) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						isTableInEditMode: isEditMode,
						inputStyles: inputStyles,
						inputType: 'text',
						editing: isEditing(record),
					}),
			  }
	);

	const handleOnOpenBadgeFromDirectory = (e, mapData: BadgeListInfoTable): void => {
		if (e.target.children[0]?.children[0]?.dataset?.id === undefined) {
			handleOnOpenBadge?.({ Id: mapData.Id, Name: mapData.Name, LastUpdated: mapData.LastUpdated });
		}
	};

	const handleOnChangeTable = (lPagination: TablePaginationConfig, filters: { Name?: string[] }, sorter: SorterResult<BadgeListInfoTable>) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortField, sortOrder } = useHandleOnChangeTableLogic()({
			clearSelection: () => {},
			filters,
			handleChangePagination: (current, pageSize) => {},
			isSearchPerformed,
			pagination: lPagination,
			shouldResetSearchColumn: false,
			sorter,
			tablePaginationSetting: pagination,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			const newPagination: PaginationSetting = {
				...pagination,
				PageNumber: current,
				PageSize: pageSize,
				SortField: sortField.toString(),
				SortDirection: sortOrder,
			};
			setPagination(newPagination);
			loadData(newPagination);
		}
	};

	const editMode = (badge: BadgeListInfo) => {
		setIsEditMode(true);
		form.setFieldsValue({
			Name: '',
			...badge,
		});
		setEditKeyReference(badge.Id);
		setTableData(prevState => createTableData(changeStateProps(badge.Id, true, prevState)));
	};

	const createTableData = (data: BadgeListInfoTable[]): BadgeListInfoTable[] => {
		if (data?.length > 0) {
			return data.map(badge => ({
				...badge,
				key: badge.Id,
				Action: createActionElement(badge as BadgeListInfoTable),
				LastUpdated: FormatDate(badge.LastUpdated, false, null, true),
			}));
		}
		return [];
	};

	const changeStateProps = (badgeId: number, editable: boolean, badgeTableData: BadgeListInfoTable[]): BadgeListInfoTable[] => {
		const cloneState: BadgeListInfoTable[] = [...badgeTableData];
		const index = cloneState.findIndex(x => x.Id === badgeId);
		if (~index) {
			const item = cloneState[index];
			cloneState.splice(index, 1, { ...item, editable: editable });
		}

		return cloneState;
	};

	const handleOnDelete = (badge: BadgeListInfoTable) => {
		ModalConfirmation({
			width: '520px',
			content: (
				<div>
					<p>
						{_('AreYouSureYouWantToDelete')} <strong>{badge.Name}</strong>
						{'?'}
					</p>
					<br />
					<span>
						{_('BadgeTemplateWillStillAvailable').replace('%1', '30')}
						{'  '}
						<span className={styles.archive}>
							<DeleteOutlined /> <span>{_('Archive')}</span>
						</span>
					</span>
				</div>
			),
			onConfirm: () => {
				setTableRefresh(true);
				handleDeleteOkConfirmation(badge.Id).finally(() => setTableRefresh(false));
			},
		});
	};

	const handleDeleteOkConfirmation = async (id: number) => {
		const response: ResponseObject = await graphicApi.archiveBadge(id);
		if (handleResponse(response)) {
			return;
		}
		loadData();
	};

	const createActionElement = (badge: BadgeListInfoTable): React.ReactNode => {
		let content: React.ReactNode = undefined;
		if (badge.editable) {
			const badgeId = badge.Id;

			content = (
				<div onClick={e => e.stopPropagation()} className={styles.actionContainer}>
					<Button id={`badgeRenameSaveButton-${badgeId}`} type='primary' onClick={() => handleOnSaveEditRow(badge)}>
						{_('Save')}
					</Button>
					<Button id={`badgeRenameCancelButton-${badgeId}`} onClick={() => handleOnCancelEditRow(badgeId)}>
						{_('Cancel')}
					</Button>
				</div>
			);
		} else {
			content = (
				<div onClick={e => e.stopPropagation()} className={styles.actionContainer} data-id={badge.Id}>
					<Button
						disabled={isDisabled()}
						shape='circle'
						icon={<EditOutlined />}
						title={getPermissionErrorMessage(canEditBadge)}
						onClick={() => editMode(badge)}
					/>
					<Button
						disabled={isDisabled()}
						shape='circle'
						icon={<DeleteOutlined />}
						title={getPermissionErrorMessage(canEditBadge)}
						onClick={() => handleOnDelete(badge)}
					/>
				</div>
			);
		}

		return <div className={styles.actions}>{content}</div>;
	};

	const handleOnSaveEditRow = async (badge: BadgeListInfoTable) => {
		const name = form.getFieldValue('Name');

		let nameError = await deviceAdminApi.renameDevice(DeviceObjectType.Badge, name, badge.Id);
		if (nameError.ResponseStatusCode === ResponseStatusCode.Success) {
			handleOnCancelEditRow(badge.Id);
		} else if (nameError.ErrorMessage) {
			form.setFields([
				{
					name: 'Name',
					errors: [nameError.ErrorMessage],
				},
			]);
			return;
		} else if (nameError.ResponseStatusCode === ResponseStatusCode.PermissionError) {
			notification['error']({
				message: nameError.ResponseErrorDescription,
			});
		}

		loadData();
	};

	const handleOnCancelEditRow = (badgeId: number) => {
		setIsEditMode(false);
		setEditKeyReference(undefined);
		setTableData(prevState => createTableData(changeStateProps(badgeId, false, prevState)));
	};

	const tablePagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		isEditMode,
		undefined,
		pagination.PageNumber,
		pagination.PageSize,
		pagination.TotalItems,
		true,
		selectedKeys,
		['10', '25'],
		10
	);

	const handleSelectAll = () => {
		setTableRefresh(true);
		graphicApi
			.getArchivedBadgeIds()
			.then(response => {
				if (!handleResponse(response)) {
					const { Entity: ids } = response;
					setSelectedKeys(ids.map(id => id as React.Key));
				}
			})
			.finally(() => {
				setTableRefresh(false);
			});
	};

	const handleSelectInvert = () => {
		const keys = tableData.map<React.Key>(td => td.key);
		const invertedKeys = invertSelectedRowKeys(keys, selectedKeys);
		setSelectedKeys(invertedKeys);
	};

	const rowSelection: TableRowSelection<BadgeListInfoTable> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(false, handleSelectAll, handleSelectInvert),
		onChange: (selectedRowKeys: React.Key[], selectedRows: BadgeListInfoTable[]) => {
			setSelectedKeys(selectedRowKeys);
		},
		selectedRowKeys: selectedKeys,
		getCheckboxProps: record => ({
			id: `archivedBadgeColumnCheckbox-${record.key}`,
			disabled: false,
			role: 'checkbox',
		}),
	};

	return (
		<div>
			<Form form={form} component={false}>
				<Table
					className={styles.badgesTable}
					columns={mergedColumns as ColumnsType<BadgeListInfoTable>}
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					dataSource={tableData}
					onChange={handleOnChangeTable}
					onRow={
						!isEditMode
							? (record: BadgeListInfoTable) => ({
									onClick: e => handleOnOpenBadgeFromDirectory(e, record),
							  })
							: null
					}
					pagination={tablePagination}
					scroll={scroll}
					size='small'
					loading={tableRefresh}
					rowSelection={selectedKeys !== undefined ? { ...rowSelection } : undefined}
				/>
			</Form>
		</div>
	);
};

export { ListView };
